// src/download/dastFormatters.js
// Shared utilities to flatten inputs
function flattenArray(arr) {
  return arr
    .map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x)))
    .join(", ");
}

function flattenObject(obj) {
  // Shallow flattener; tailor as needed
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[k] = typeof v === "object" && v !== null ? JSON.stringify(v) : v;
  });
  return out;
}

// Default CVSS values (same as current Run-1 logic, trimmed for brevity here)
function getDefaultCvssValues(severity) {
  const s = (severity || "").toLowerCase();
  const defaults = {
    critical: {
      "Attack Vector (AV)": "Network (N)",
      "Attack Complexity (AC)": "Low (L)",
      "Privileges Required (PR)": "Low (L)",
      "User Interaction (UI)": "None (N)",
      "Scope (S)": "Changed (C)",
      "Confidentiality Impact (C)": "High (H)",
      "Integrity Impact (I)": "High (H)",
      "Availability Impact (A)": "High (H)",
      "Exploit Code Maturity (E)": "Proof-of-Concept (P)",
      "Remediation Level (RL)": "Official Fix (O)",
      "Report Confidence (RC)": "Confirmed (C)",
      "Modified Attack Vector (MAV)": "Network (N)",
      "Modified Attack Complexity (MAC)": "Low (L)",
      "Modified Privileges Required (MPR)": "Low (L)",
      "Modified User Interaction (MUI)": "None (N)",
      "Modified Scope (MS)": "Changed (C)",
      "Modified Confidentiality (MC)": "High (H)",
      "Modified Integrity (MI)": "High (H)",
      "Modified Availability (MA)": "High (H)",
      "Confidentiality Requirement (CR)": "High (H)",
      "Integrity Requirement (IR)": "High (H)",
      "Availability Requirement (AR)": "High (H)",
    },
    high: {
      "Attack Vector (AV)": "Network (N)",
      "Attack Complexity (AC)": "Low (L)",
      "Privileges Required (PR)": "Low (L)",
      "User Interaction (UI)": "None (N)",
      "Scope (S)": "Unchanged (U)",
      "Confidentiality Impact (C)": "High (H)",
      "Integrity Impact (I)": "Low (L)",
      "Availability Impact (A)": "High (H)",
      "Exploit Code Maturity (E)": "Proof-of-Concept (P)",
      "Remediation Level (RL)": "Official Fix (O)",
      "Report Confidence (RC)": "Reasonable (R)",
      "Modified Attack Vector (MAV)": "Network (N)",
      "Modified Attack Complexity (MAC)": "Low (L)",
      "Modified Privileges Required (MPR)": "Low (L)",
      "Modified User Interaction (MUI)": "None (N)",
      "Modified Scope (MS)": "Unchanged (U)",
      "Modified Confidentiality (MC)": "High (H)",
      "Modified Integrity (MI)": "High (H)",
      "Modified Availability (MA)": "Low (L)",
      "Confidentiality Requirement (CR)": "High (H)",
      "Integrity Requirement (IR)": "High (H)",
      "Availability Requirement (AR)": "Medium (M)",
    },
    medium: {
      "Attack Vector (AV)": "Network (N)",
      "Attack Complexity (AC)": "Low (L)",
      "Privileges Required (PR)": "Low (L)",
      "User Interaction (UI)": "Required (R)",
      "Scope (S)": "Unchanged (U)",
      "Confidentiality Impact (C)": "Low (L)",
      "Integrity Impact (I)": "Low (L)",
      "Availability Impact (A)": "Low (L)",
      "Exploit Code Maturity (E)": "Proof-of-Concept (P)",
      "Remediation Level (RL)": "Official Fix (O)",
      "Report Confidence (RC)": "Reasonable (R)",
      "Modified Attack Vector (MAV)": "Network (N)",
      "Modified Attack Complexity (MAC)": "Low (L)",
      "Modified Privileges Required (MPR)": "Low (L)",
      "Modified User Interaction (MUI)": "Required (R)",
      "Modified Scope (MS)": "Unchanged (U)",
      "Modified Confidentiality (MC)": "Low (L)",
      "Modified Integrity (MI)": "Low (L)",
      "Modified Availability (MA)": "Low (L)",
      "Confidentiality Requirement (CR)": "Medium (M)",
      "Integrity Requirement (IR)": "Medium (M)",
      "Availability Requirement (AR)": "Medium (M)",
    },
  };
  return defaults[s] || defaults.medium;
}

function getDefaultVulnStatus(severity) {
  const s = (severity || "").toLowerCase();
  return ["critical", "high", "medium"].includes(s) ? "TP" : "FP";
}

// Common transform: from dastScanData -> flattened records + instances list
function baseTransform(dastScanData) {
  return dastScanData
    .filter((x) => x.riskdesc !== "Informational")
    .map((scanResult) => {
      const flattened = {};
      Object.entries(scanResult).forEach(([key, value]) => {
        if (Array.isArray(value)) flattened[key] = flattenArray(value);
        else if (typeof value === "object" && value !== null)
          flattened[key] = flattenObject(value);
        else flattened[key] = value;
      });

      const processedInstances =
        scanResult.instances?.map((inst, i) => ({
          url: inst.url || "",
          evidence: inst.evidence || "N/A",
          index: i + 1,
        })) || [];

      const severity = flattened.riskdesc || "Low";
      return { flattened, processedInstances, severity };
    });
}

// Format for Run-1 (updated to return flat array)
export function formatForRun1(dastScanData) {
  const records = baseTransform(dastScanData);
  const formatted = records.map(
    ({ flattened, processedInstances, severity }) => {
      const defaults = getDefaultCvssValues(severity);
      return {
        "Vulnerability Name": flattened.alert || "",
        Description: flattened.desc || "",
        Instances:
          processedInstances.map((x) => `${x.index}. ${x.url}`).join("\n") ||
          "N/A",
        Evidence:
          processedInstances
            .map((x) => `${x.index}. ${x.evidence}`)
            .join("\n") || "N/A",
        "Vulnerability Status": getDefaultVulnStatus(severity),
        Severity: severity,
        Remediation: flattened.solution || "",
        "Vulnerability Identified Date": "",
        ...defaults,
        instances: processedInstances,
      };
    }
  );

  // 🔥 FIX: Return flat array instead of nested
  return formatted; // Changed from [formatted] to formatted
}

// Format for Run-2 (already returns flat array, no change needed)
export function formatForRun2(dastScanData) {
  const records = baseTransform(dastScanData);
  const formatted = records.map(
    ({ flattened, processedInstances, severity }) => {
      return {
        "Vulnerability Name": flattened.alert || "",
        Description: flattened.desc || "",
        Instances:
          processedInstances.map((x) => `${x.index}. ${x.url}`).join("\n") ||
          "N/A",
        Evidence:
          processedInstances
            .map((x) => `${x.index}. ${x.evidence}`)
            .join("\n") || "N/A",
        "Vulnerability Status": getDefaultVulnStatus(severity),
        Severity: severity,
        Remediation: flattened.solution || "",
        "Vulnerability Identifed Date": "",
        "Vulnerability Remediation Status": "",
        "Vulnerability Remediation Date": "",
        instances: processedInstances,
      };
    }
  );

  return formatted; // Already correct
}
